import {
  BookOpen,
  Building2,
  Clock,
  FilePlus2,
  FileText,
  FolderKanban,
  HardHat,
  KeyRound,
  LayoutDashboard,
  List,
  LogIn,
  Settings,
  ShieldCheck,
  Smartphone,
  Tags,
  UserCircle,
  Users,
  type LucideIcon
} from "lucide-react";
import type { AuthUser } from "@/core/auth/session";
import { DOCUMENT_PERMISSIONS } from "@/core/auth/document-permissions";
import {
  EMPLOYEE_PERSONAL_PROFILE_READ_PERMISSION,
  EMPLOYEE_ROLE_UPDATE_PERMISSION,
  hasPermission as userHasPermission
} from "@/core/auth/permissions";

const SYSTEM_FULL_ACCESS_PERMISSION = "system.full_access";
const EMPLOYEE_ACCOUNT_PERMISSIONS = [
  "employees.read_all",
  "employees.create",
  "employees.manage_scoped",
  EMPLOYEE_PERSONAL_PROFILE_READ_PERMISSION,
  EMPLOYEE_ROLE_UPDATE_PERMISSION,
  "managers.create",
  "managers.manage"
];
const ORGANIZATION_PERMISSIONS = [
  "branches.read",
  "branches.create",
  "branches.update",
  "branches.delete",
  "departments.read"
];
const ATTENDANCE_PERMISSIONS = [
  "attendance.self",
  "attendance.manage_scoped",
  "attendance.review"
];
const PROJECT_PERMISSIONS = [
  "projects.read",
  "projects.create",
  "projects.update",
  "projects.archive",
  "projects.members",
  "projects.members_manage",
  "projects.status",
  "projects.manage"
];
const DOCUMENT_READ_PERMISSIONS = [
  DOCUMENT_PERMISSIONS.books.read,
  DOCUMENT_PERMISSIONS.types.read,
  DOCUMENT_PERMISSIONS.issuingAuthorities.read,
  DOCUMENT_PERMISSIONS.incoming.read,
  DOCUMENT_PERMISSIONS.outgoing.read,
  DOCUMENT_PERMISSIONS.outgoing.process,
  DOCUMENT_PERMISSIONS.outgoing.dispatch,
  DOCUMENT_PERMISSIONS.internal.read
];
const DOCUMENT_CREATE_PERMISSIONS = [
  DOCUMENT_PERMISSIONS.incoming.create,
  DOCUMENT_PERMISSIONS.outgoing.create
];

export type AppModuleId =
  | "admin"
  | "hr"
  | "projects"
  | "documents"
  | "internalDocuments";
export type NavGroupId =
  | "B1"
  | "HR"
  | "PROJECTS"
  | "DOCUMENTS"
  | "INTERNAL_DOCUMENTS";

export type AppModule = {
  id: AppModuleId;
  key: AppModuleId;
  label: string;
  name: string;
  description: string;
  icon: LucideIcon;
  color: string;
  bg: string;
  path: string;
  available: boolean;
  groupId: NavGroupId;
};

export type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  requiredPermissions?: string[];
  requiredAllPermissions?: string[];
};

export type NavGroup = {
  id: NavGroupId;
  label: string;
  topLabel: string;
  items: NavItem[];
};

export const PERSONAL_PROFILE_PATH = "/he-thong/ho-so";
export const EMPLOYEE_ACCOUNTS_PATH = "/nhan-su/tai-khoan";
const LEGACY_EMPLOYEE_ACCOUNTS_PATH = "/he-thong/tai-khoan";

export const APP_MODULES: AppModule[] = [
  {
    id: "admin",
    key: "admin",
    label: "Quản trị hệ thống",
    name: "Quản trị",
    description: "Bảng điều khiển, tài khoản, cấu hình",
    icon: Settings,
    color: "text-violet-600",
    bg: "bg-violet-50",
    path: "/he-thong/dashboard",
    available: true,
    groupId: "B1"
  },
  {
    id: "hr",
    key: "hr",
    label: "Nhân sự",
    name: "Nhân sự",
    description: "Tài khoản người dùng, dữ liệu chấm công",
    icon: HardHat,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    path: "/nhan-su/lich-su-cham-cong",
    available: true,
    groupId: "HR"
  },
  {
    id: "projects",
    key: "projects",
    label: "Dự án",
    name: "Dự án",
    description: "Dự án, công trình và ngân sách",
    icon: FolderKanban,
    color: "text-sky-600",
    bg: "bg-sky-50",
    path: "/du-an/quan-ly-du-an",
    available: true,
    groupId: "PROJECTS"
  },
  {
    id: "internalDocuments",
    key: "internalDocuments",
    label: "Văn bản nội bộ",
    name: "Văn bản nội bộ",
    description: "Phân phối, phê duyệt và xác nhận tiếp nhận",
    icon: FileText,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    path: "/van-ban/noi-bo",
    available: true,
    groupId: "INTERNAL_DOCUMENTS"
  },
  {
    id: "documents",
    key: "documents",
    label: "Văn bản",
    name: "Văn bản",
    description: "Văn bản đến, ra ngoài và quy trình duyệt",
    icon: FileText,
    color: "text-amber-600",
    bg: "bg-amber-50",
    path: "/van-ban",
    available: true,
    groupId: "DOCUMENTS"
  }
];

export function getModuleByPath(pathnameOrRedirect?: string | null) {
  const pathname = getPathname(pathnameOrRedirect);
  if (!pathname) return null;

  return (
    APP_MODULES.find(
      (module) =>
        module.available &&
        (pathname === module.path || pathname.startsWith(`${module.path}/`))
    ) ??
    APP_MODULES.find((module) =>
      NAV_GROUPS.find(
        (group) =>
          group.id === module.groupId &&
          group.items.some(
            (item) => pathname === item.to || pathname.startsWith(`${item.to}/`)
          )
      )
    ) ??
    null
  );
}

export function canUserAccessPath(
  user: Pick<AuthUser, "permissions">,
  pathnameOrRedirect?: string | null
) {
  const pathname = getPathname(pathnameOrRedirect);
  if (pathname === LEGACY_EMPLOYEE_ACCOUNTS_PATH) {
    return canUserAccessPath(user, EMPLOYEE_ACCOUNTS_PATH);
  }
  if (isPersonalProfilePath(pathname) || pathname === "/ung-dung") return true;
  if (pathname === "/van-ban/noi-bo/tao-moi") {
    return userHasPermission(user, DOCUMENT_PERMISSIONS.internal.create);
  }
  if (isInternalDocumentDetailPath(pathname)) return true;

  const navItem = getNavItemByPath(pathnameOrRedirect);
  if (navItem) return canUserAccessNavItem(user, navItem);

  return Boolean(
    pathname?.startsWith("/van-ban") &&
      hasAnyPermission(user, DOCUMENT_READ_PERMISSIONS)
  );
}

function isInternalDocumentDetailPath(pathname?: string | null) {
  return Boolean(
    pathname &&
      /^\/van-ban\/noi-bo\/[^/]+$/.test(pathname) &&
      pathname !== "/van-ban/noi-bo/tao-moi"
  );
}

export function isPersonalProfilePath(pathnameOrRedirect?: string | null) {
  const pathname = getPathname(pathnameOrRedirect);
  if (!pathname) return false;

  return (
    pathname === PERSONAL_PROFILE_PATH ||
    pathname.startsWith(`${PERSONAL_PROFILE_PATH}/`)
  );
}

function getPathname(pathnameOrRedirect?: string | null) {
  if (!pathnameOrRedirect) return null;

  try {
    return new URL(pathnameOrRedirect, "http://erp.local").pathname;
  } catch {
    return pathnameOrRedirect.split("?")[0] || pathnameOrRedirect;
  }
}

function getNavItemByPath(pathnameOrRedirect?: string | null) {
  const pathname = getPathname(pathnameOrRedirect);
  if (!pathname) return null;

  return (
    NAV_GROUPS.flatMap((group) => group.items)
      .filter(
        (item) => pathname === item.to || pathname.startsWith(`${item.to}/`)
      )
      .sort((left, right) => right.to.length - left.to.length)[0] ?? null
  );
}

export function canUserAccessNavItem(
  user: Pick<AuthUser, "permissions">,
  item: NavItem
) {
  return (
    hasAnyPermission(user, item.requiredPermissions) &&
    hasAllPermissions(user, item.requiredAllPermissions)
  );
}

function hasAnyPermission(
  user: Pick<AuthUser, "permissions">,
  requiredPermissions?: string[]
) {
  if (!requiredPermissions?.length) return false;
  return requiredPermissions.some((permission) =>
    userHasPermission(user, permission)
  );
}

function hasAllPermissions(
  user: Pick<AuthUser, "permissions">,
  requiredPermissions?: string[]
) {
  return (
    !requiredPermissions?.length ||
    requiredPermissions.every((permission) =>
      userHasPermission(user, permission)
    )
  );
}

export function getVisibleNavGroupsForUser(
  user: Pick<AuthUser, "permissions">
) {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => canUserAccessNavItem(user, item))
  })).filter((group) => group.items.length > 0);
}

export const PERSONAL_NAV_ITEMS: NavItem[] = [
  { to: PERSONAL_PROFILE_PATH, label: "Tài khoản của tôi", icon: UserCircle }
];

export const NAV_GROUPS: NavGroup[] = [
  {
    id: "B1",
    label: "B1 — Quản lý Hệ thống",
    topLabel: "Hệ thống",
    items: [
      {
        to: "/he-thong/dashboard",
        label: "Bảng điều khiển tổng quan",
        icon: LayoutDashboard,
        requiredPermissions: [SYSTEM_FULL_ACCESS_PERMISSION]
      },
      {
        to: "/he-thong/permission",
        label: "Quyền",
        icon: KeyRound,
        requiredPermissions: [SYSTEM_FULL_ACCESS_PERMISSION]
      },
      {
        to: "/he-thong/role",
        label: "Vai trò",
        icon: ShieldCheck,
        requiredPermissions: [SYSTEM_FULL_ACCESS_PERMISSION]
      },
      {
        to: "/he-thong/quyen-truy-cap-web",
        label: "Quyền truy cập ERP Web",
        icon: ShieldCheck,
        requiredPermissions: [SYSTEM_FULL_ACCESS_PERMISSION]
      },
      {
        to: "/he-thong/dang-nhap",
        label: "Đăng nhập 2FA",
        icon: LogIn,
        requiredPermissions: [SYSTEM_FULL_ACCESS_PERMISSION]
      },
      {
        to: "/he-thong/phien-ban-mobile",
        label: "Phiên bản mobile",
        icon: Smartphone,
        requiredPermissions: [SYSTEM_FULL_ACCESS_PERMISSION]
      },
      {
        to: "/he-thong/cau-hinh",
        label: "Cấu hình hệ thống",
        icon: Settings,
        requiredPermissions: ["erp.configure"]
      }
    ]
  },
  {
    id: "HR",
    label: "Nhân sự",
    topLabel: "Nhân sự",
    items: [
      {
        to: EMPLOYEE_ACCOUNTS_PATH,
        label: "Tài khoản người dùng",
        icon: Users,
        requiredPermissions: EMPLOYEE_ACCOUNT_PERMISSIONS,
        requiredAllPermissions: ["departments.read"]
      },
      {
        to: "/nhan-su/co-cau-to-chuc",
        label: "Cơ cấu tổ chức",
        icon: Building2,
        requiredPermissions: ORGANIZATION_PERMISSIONS,
        requiredAllPermissions: ["departments.read"]
      },
      {
        to: "/nhan-su/lich-su-cham-cong",
        label: "Lịch sử chấm công",
        icon: Clock,
        requiredPermissions: ATTENDANCE_PERMISSIONS
      },
      {
        to: "/nhan-su/cham-cong-bu",
        label: "Chấm công bù",
        icon: Clock,
        requiredPermissions: ["attendance.manage_scoped"]
      },
      {
        to: "/nhan-su/cham-cong",
        label: "Báo cáo tổng hợp",
        icon: Clock,
        requiredPermissions: ["attendance.manage_scoped", "attendance.review"]
      }
    ]
  },
  {
    id: "PROJECTS",
    label: "Dự án",
    topLabel: "Dự án",
    items: [
      {
        to: "/du-an/quan-ly-du-an",
        label: "Quản lý dự án",
        icon: FolderKanban,
        requiredPermissions: PROJECT_PERMISSIONS
      },
      {
        to: "/du-an/danh-muc-hang-muc",
        label: "Danh mục hạng mục",
        icon: List,
        requiredPermissions: ["projects.read"]
      }
    ]
  },
  {
    id: "DOCUMENTS",
    label: "Văn bản",
    topLabel: "Văn bản",
    items: [
      {
        to: "/van-ban/so-van-ban",
        label: "Sổ Văn Bản",
        icon: BookOpen,
        requiredPermissions: [DOCUMENT_PERMISSIONS.books.read]
      },
      {
        to: "/van-ban/loai-van-ban",
        label: "Loại Văn Bản",
        icon: Tags,
        requiredPermissions: [DOCUMENT_PERMISSIONS.types.read]
      },
      {
        to: "/van-ban/noi-ban-hanh",
        label: "Nơi ban hành",
        icon: Building2,
        requiredPermissions: [DOCUMENT_PERMISSIONS.issuingAuthorities.read]
      },
      {
        to: "/van-ban/tao-moi",
        label: "Tạo văn bản",
        icon: FilePlus2,
        requiredPermissions: DOCUMENT_CREATE_PERMISSIONS
      },
      {
        to: "/van-ban",
        label: "Văn bản",
        icon: FileText,
        requiredPermissions: [
          DOCUMENT_PERMISSIONS.incoming.read,
          DOCUMENT_PERMISSIONS.outgoing.read,
          DOCUMENT_PERMISSIONS.outgoing.process,
          DOCUMENT_PERMISSIONS.outgoing.dispatch
        ]
      }
    ]
  },
  {
    id: "INTERNAL_DOCUMENTS",
    label: "Văn bản nội bộ",
    topLabel: "Văn bản nội bộ",
    items: [
      {
        to: "/van-ban/noi-bo",
        label: "Danh sách văn bản",
        icon: FileText,
        requiredPermissions: [DOCUMENT_PERMISSIONS.internal.read]
      },
      {
        to: "/van-ban/noi-bo/tao-moi",
        label: "Tạo văn bản",
        icon: FilePlus2,
        requiredPermissions: [DOCUMENT_PERMISSIONS.internal.create]
      }
    ]
  }
];
